import { Injectable } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';

// import { Fuse } from 'fuse.js';
import * as Fuse from 'fuse.js';

import { AuthorizationService } from '../authorization/authorization.service';
import { IoService } from '../io/io.service';
import { ScreenService } from '../screen/screen.service';
import { AuthenticationService } from '../authentication/authentication.service';
import { ClientSettingsService } from '../client-settings/client-settings.service';

@Injectable({
  providedIn: 'root'
})
export class LocationService {

  private activeLocation;
  private locationOptions;
  private locations;
  private indexedLocations;
  private allLocation = {
    name: 'All Locations',
    _id: null
  };
  private searchText;

  private activeScreen;

  private activeLocationSet = new Subject<any>();
  private locationsSet = new Subject<any>();

  private activeScreenSet: Subscription;

  constructor(
    private authorizationService: AuthorizationService,
    private ioService: IoService,
    private screenService: ScreenService,
    private authenticationService: AuthenticationService,
    private clientSettingsService: ClientSettingsService
  ) {
    // The warehouse picker flag arrives with the client settings, usually
    // after the locations themselves — re-run the filter so the header menu
    // (and the single-selectable-location rule) pick it up.
    this.clientSettingsService.clientSettingsLoadedObservable().subscribe(settings => {
      if (settings && this.locationOptions) {
        this.filterLocations();
      }
    });

    this.authenticationService.authenticationStateChangedObservable().subscribe(authenticatedId => {
      if (authenticatedId) {
        // A PIN unlock on an authorized device always starts at that
        // device's location, even when the app was not reloaded in between
        // (lock → unlock keeps this singleton alive).
        if (this.authenticationService.isDeviceSession()) {
          this.activeLocation = null;
        }
        this.loadLocations();
      }
    });

    if (this.authenticationService.isSignedIn()) {
      this.loadLocations();
    }
  }

  loadLocations = async () => {
    let locationsResponse = await this.ioService.post('/location/getActiveUserLocations');
    this.locationOptions = locationsResponse;

    this.indexedLocations = {};
    for (let location of this.locationOptions) {
      this.indexedLocations[location._id] = location;
    }

    this.filterLocations();
    return;
  };

  /*
  async loadLocations(): Promise<void> {
    return new Promise<void>( (resolve) => {
      this.ioService.post('/location/getActiveUserLocations').then((locationsResponse: any) => {
        this.locationOptions = locationsResponse;

        this.indexedLocations = {};
        for (let location of this.locationOptions) {
          this.indexedLocations[location._id] = location;
        }

        // Get the active screen
        this.activeScreen = this.screenService.getActiveScreen();

        // Filter now
        this.filterLocations();

        // Listen for when the screen changes
        this.activeScreenSet = this.screenService.activeScreenSetObservable().subscribe(activeScreen => {
          this.activeScreen = activeScreen;
          // this.filterLocations(activeScreen);
          this.filterLocations();
        });
        resolve();
      });
    });
  }
  */

  filterLocations() {
    this.locations = [this.allLocation, ...this.locationOptions];

    // If the active location is not in the valid set, select the first valid location.
    // On startup (no active location yet), a location flagged as the user's default
    // (User.defaultLocationId, surfaced by getActiveUserLocations) wins instead.
    // This only runs at initialization — it never overrides a manual selection.
    if ((this.locations.indexOf(this.activeLocation) == -1) || (!this.activeLocation)) {
      // A PIN sign-in on an authorized device is bound to that device's
      // location, which beats the user's own default for the initial pick.
      const sessionLocationId = this.authenticationService.getSessionLocationId();
      const sessionLocation = sessionLocationId
        ? this.locations.find(location => location._id === sessionLocationId)
        : null;
      const defaultLocation = this.locations.find(location => location.default === true);
      this.setActiveLocation(sessionLocation || defaultLocation || this.locations[0]);
    }

    // If there is only one selectable location and it is not the active location, select it
    let selectableLocations = this.locations.filter(location => {
      return this.isSelectable(location) && location._id !== null;
    });
    if ((selectableLocations.length == 1) && (selectableLocations[0] != this.activeLocation)) {
      this.setActiveLocation(selectableLocations[0]);
    }

    // The search narrows only what the header menu displays (emitted on the
    // subject); business logic reading getLocations()/getLocationsByType()
    // always gets the full list.
    let displayLocations = this.locations;
    if (this.searchText) {
      let fuse = new Fuse(this.locations, {
        shouldSort: true,
        threshold: 0.3,
        location: 0,
        distance: 100,
        maxPatternLength: 16,
        minMatchCharLength: 1,
        keys: [
          'name'
        ]
      });

      displayLocations = fuse.search(this.searchText);
    }

    this.locationsSet.next(displayLocations);
  }

  // Whether a location may be chosen in the header picker: "All Locations",
  // retail locations, and — when the client enables settings.warehouseLocationPicker —
  // warehouse locations. Vendor and adjustment locations are never selectable.
  isSelectable(location): boolean {
    if (!location) {
      return false;
    }
    if (location._id === null || location.type === 'retail') {
      return true;
    }
    return location.type === 'warehouse' && this.clientSettingsService.isWarehouseLocationPickerEnabled();
  }

  setSearchText(searchText) {
    this.searchText = searchText;
    this.filterLocations();
  }

  getSearchText() {
    return this.searchText;
  }

  setActiveLocation(location) {
    console.log('set active location:', location);
    this.activeLocation = location;
    this.activeLocationSet.next(this.activeLocation);
  }

  getActiveLocation() {
    return this.activeLocation;
  }

  getActiveLocationAsync = async () => {
    while ( !this.activeLocation ) {
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    return this.activeLocation;
  }

  getActiveLocationId() {
    return this.activeLocation._id;
  }

  getLocations() {
    return this.locations;
  }

  getLocationIds() {
    return this.getLocations().map(location => {
      return location._id;
    });
  }

  // `type` may be a single type string or an array of types (e.g. a
  // PurchaseType whose targetType is ['retail', 'warehouse'] so physical
  // counts can run at either kind of location).
  async getLocationsByType(type): Promise<Array<any>> {
    if ( !this.locations ) {
      await this.loadLocations();
    }
    return this.locations.filter((location) => {
      return Array.isArray(type) ? type.includes(location.type) : location.type === type;
    });
  }

  async getLocationsObjectByType(type) {
    let locations = await this.getLocationsByType(type);
    let locationsObject = {};
    for (let location of locations) {
      locationsObject[location._id] = location;
    }
    return locationsObject;
  }

  getLocation(locationId) {
    if (this.indexedLocations && this.indexedLocations[locationId]) {
      return this.indexedLocations[locationId];
    } else {
      return {
        name: 'Unknown Location'
      };
    }
  }

  activeLocationSetObservable() : Observable<any> {
    return this.activeLocationSet.asObservable();
  }

  locationsSetObservable(): Observable<any> {
    return this.locationsSet.asObservable();
  }

}
